/** Formatting helpers shared by the studio UI. */

/** `83.4` -> `"01:23"` (or `"01:23.40"` with centisecond precision). */
export function formatTime(seconds: number, options: { showMs?: boolean } = {}): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  const minutes = Math.floor(safe / 60)
  const secs = Math.floor(safe % 60)
  const mm = String(minutes).padStart(2, '0')
  const ss = String(secs).padStart(2, '0')
  if (!options.showMs) return `${mm}:${ss}`
  const cs = Math.min(99, Math.floor((safe - Math.floor(safe)) * 100))
  return `${mm}:${ss}.${String(cs).padStart(2, '0')}`
}

/** `735` -> `"12:15"`, `12.5` -> `"12.5s"`, `95` -> `"1m 35s"`. */
export function formatSeconds(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  if (safe < 60) {
    return `${Math.round(safe * 10) / 10}s`
  }
  const minutes = Math.floor(safe / 60)
  const rest = Math.round(safe % 60)
  return `${minutes}m ${String(rest).padStart(2, '0')}s`
}

/** `1536` -> `"1.5 KiB"`, `26214400` -> `"25 MiB"`. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB']
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / 1024 ** exponent
  const digits = exponent === 0 ? 0 : value >= 100 ? 0 : value >= 10 ? 1 : 2
  return `${value.toFixed(digits)} ${units[exponent]}`
}

/** `9000000` -> `"9 Mbps"`. */
export function formatBitrate(bitsPerSecond: number): string {
  if (!Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return '—'
  if (bitsPerSecond >= 1_000_000) {
    const mbps = bitsPerSecond / 1_000_000
    return `${Math.round(mbps * 10) / 10} Mbps`
  }
  return `${Math.round(bitsPerSecond / 1000)} kbps`
}

/** `9` -> `"$9"`, `8.5` -> `"$8.50"`. */
export function formatUsd(amount: number): string {
  const safe = Number.isFinite(amount) ? amount : 0
  return `$${Number.isInteger(safe) ? safe.toFixed(0) : safe.toFixed(2)}`
}

/** `0.4237` -> `"42%"`. */
export function formatPercent(fraction: number, digits = 0): string {
  const safe = Number.isFinite(fraction) ? fraction : 0
  return `${(safe * 100).toFixed(digits)}%`
}

/** Clamp a number into an inclusive range; non-finite input falls back to `min`. */
export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, value))
}
